import { ChevronDownIcon, PlayCircleIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import {
    DiscoverTagResponse,
    LessonResponse,
} from '../../../server/response/listeningResponseScheme';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import modalStore from '../../stores/modalStore';
import { CommonLink } from '../../views/common/CommonLink';
import LessonIntroModalContent from '../lesson/modal/LessonIntroModalContent';

class ViewStore {
    tags: DiscoverTagResponse[] = [];
    selectedTagHash: string | null = null;
    discoverLessons: LessonResponse[] = [];

    isLoadingTags = true;
    isLoadingLessons = false;
    isLoadingMore = false;
    isEnd = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetchTags() {
        this.isLoadingTags = true;

        try {
            const tags = await apiClient.discover.public.getTagList.$query();

            runInAction(() => {
                this.tags = tags;
                this.isLoadingTags = false;

                // 첫 번째 태그 자동 선택
                if (tags.length > 0) {
                    this.selectTag(tags[0].hash);
                }
            });
        } catch {
            runInAction(() => {
                this.tags = [];
                this.isLoadingTags = false;
            });
        }
    }

    selectTag(tagHash: string) {
        if (this.selectedTagHash === tagHash) {
            return;
        }

        this.selectedTagHash = tagHash;
        this.discoverLessons = [];
        this.isEnd = false;
        this.fetchLessons();
    }

    async fetchLessons() {
        if (!this.selectedTagHash) {
            return;
        }

        this.isLoadingLessons = true;

        try {
            const result = await apiClient.discover.public.getDiscoverList.$query({
                tagHash: this.selectedTagHash,
                fromLessonHash: null,
            });

            runInAction(() => {
                this.discoverLessons = result.items;
                this.isEnd = result.isEnd;
                this.isLoadingLessons = false;
            });
        } catch {
            runInAction(() => {
                this.discoverLessons = [];
                this.isEnd = true;
                this.isLoadingLessons = false;
            });
        }
    }

    async fetchMore() {
        if (!this.selectedTagHash || this.isEnd || this.isLoadingMore) {
            return;
        }

        const lastLesson = this.discoverLessons[this.discoverLessons.length - 1];
        if (!lastLesson) {
            return;
        }

        this.isLoadingMore = true;

        try {
            const result = await apiClient.discover.public.getDiscoverList.$query({
                tagHash: this.selectedTagHash,
                fromLessonHash: lastLesson.hash,
            });

            runInAction(() => {
                this.discoverLessons = [...this.discoverLessons, ...result.items];
                this.isEnd = result.isEnd;
                this.isLoadingMore = false;
            });
        } catch {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }
}

// 발견 카드 컴포넌트
const DiscoverCard = observer(({ lesson }: { lesson: LessonResponse }) => {
    const onClickCard = async (e: React.MouseEvent) => {
        e.preventDefault();
        await modalStore.custom({
            renderComponent: ({ modalId }) => (
                <LessonIntroModalContent modalId={modalId} lessonHash={lesson.hash} />
            ),
            showCloseButton: true,
        });
    };

    return (
        <CommonLink
            to={`/lesson/${lesson.hash}`}
            className="flex flex-col gap-2 text-left"
            onClick={onClickCard}
        >
            <div className="aspect-video w-full overflow-hidden rounded-md">
                {lesson.video.thumbnail ? (
                    <img
                        src={lesson.video.thumbnail}
                        alt={lesson.titleKo || lesson.video.title}
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="bg-base-300 flex h-full w-full items-center justify-center">
                        <PlayCircleIcon className="text-base-content/30 size-8" />
                    </div>
                )}
            </div>
            <p className="text-base-content/80 line-clamp-2 text-sm leading-tight">
                {lesson.titleKo || lesson.title || lesson.video.title}
            </p>
        </CommonLink>
    );
});

// 스켈레톤 카드 컴포넌트
const DiscoverCardSkeleton = () => {
    return (
        <div className="flex flex-col gap-2">
            <div className="skeleton aspect-video w-full rounded-md" />
            <div className="skeleton h-4 w-3/4" />
        </div>
    );
};

// 태그 탭 컴포넌트
const TagTabs = observer(({ viewStore }: { viewStore: ViewStore }) => {
    return (
        <div className="flex gap-2 overflow-x-auto pb-2">
            {viewStore.tags.map((tag) => (
                <button
                    key={tag.hash}
                    className={twMerge(
                        'btn btn-sm whitespace-nowrap',
                        viewStore.selectedTagHash === tag.hash
                            ? 'btn-action-neutral'
                            : 'btn-action-soft',
                    )}
                    onClick={() => {
                        viewStore.selectTag(tag.hash);
                    }}
                >
                    #{tag.name}
                </button>
            ))}
        </div>
    );
});

const DiscoverList = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetchTags();
    }, [viewStore]);

    // 태그 로딩 중
    if (viewStore.isLoadingTags) {
        return (
            <div className="flex flex-col gap-4">
                <div className="flex gap-2">
                    <div className="skeleton h-8 w-20 rounded-lg" />
                    <div className="skeleton h-8 w-24 rounded-lg" />
                    <div className="skeleton h-8 w-16 rounded-lg" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <DiscoverCardSkeleton />
                    <DiscoverCardSkeleton />
                    <DiscoverCardSkeleton />
                </div>
            </div>
        );
    }

    // 태그 없음
    if (viewStore.tags.length === 0) {
        return (
            <p className="text-base-content/50 py-4 text-center text-sm">
                아직 발견할 레슨이 없습니다
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* 태그 탭 */}
            <TagTabs viewStore={viewStore} />

            {/* 레슨 목록 */}
            {viewStore.isLoadingLessons ? (
                <div className="grid grid-cols-3 gap-4">
                    <DiscoverCardSkeleton />
                    <DiscoverCardSkeleton />
                    <DiscoverCardSkeleton />
                </div>
            ) : viewStore.discoverLessons.length === 0 ? (
                <p className="text-base-content/50 py-4 text-center text-sm">
                    이 태그에 레슨이 없습니다
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-4">
                        {viewStore.discoverLessons.map((lesson) => (
                            <DiscoverCard key={lesson.hash} lesson={lesson} />
                        ))}
                    </div>
                    {/* 더보기 버튼 */}
                    {!viewStore.isEnd && (
                        <button
                            className="btn btn-ghost btn-sm text-base-content/60"
                            onClick={() => {
                                viewStore.fetchMore();
                            }}
                            disabled={viewStore.isLoadingMore}
                        >
                            더보기
                            {viewStore.isLoadingMore ? (
                                <span className="loading loading-ring loading-xs"></span>
                            ) : (
                                <ChevronDownIcon className="size-4" />
                            )}
                        </button>
                    )}
                </>
            )}
        </div>
    );
});

export default DiscoverList;
